import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Page-level tab state kept in the URL (`?tab=slug`) so a tab can be deep
 * linked and survives reload and back navigation.
 *
 * Pages used to hold the active tab in local state, which reset on every
 * navigation. This mirrors the `?tab=` convention the System and Brands &
 * Vendors shells already use. Unknown or missing values fall back to
 * `fallback`; selecting the fallback removes the param to keep URLs clean.
 * Other search params on the page are preserved.
 */
export function useUrlTab<T extends string>(
  keys: readonly T[],
  fallback: T,
  param = 'tab',
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(param);
  const value = raw !== null && (keys as readonly string[]).includes(raw) ? (raw as T) : fallback;

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === fallback) params.delete(param);
          else params.set(param, next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, fallback, param],
  );

  return [value, setValue];
}
